// SPDX-FileCopyrightText: 2017-2024 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import React, { useState } from 'react'
import styled from 'styled-components'

import type { Action } from 'lib-common/generated/action'
import type { DaycareGroupResponse } from 'lib-common/generated/api-types/daycare'
import type {
  DaycareAclRow,
  DaycareId
} from 'lib-common/generated/api-types/shared'
import LocalDate from 'lib-common/local-date'
import HorizontalLine from 'lib-components/atoms/HorizontalLine'
import Checkbox from 'lib-components/atoms/form/Checkbox'
import MultiSelect from 'lib-components/atoms/form/MultiSelect'
import { FixedSpaceColumn } from 'lib-components/layout/flex-helpers'
import { PersonName } from 'lib-components/molecules/PersonNames'
import DatePicker from 'lib-components/molecules/date-picker/DatePicker'
import { MutateFormModal } from 'lib-components/molecules/modals/FormModal'
import { Label } from 'lib-components/typography'
import { defaultMargins } from 'lib-components/white-space'

import { useTranslation } from '../../../../state/i18n'
import { updateGroupAclMutation } from '../../queries'

import { useGroupOptions } from './common'

interface Props {
  onClose: () => void
  permittedActions: Action.Unit[]
  endDateEditable: boolean
  unitId: DaycareId
  groups: Record<string, DaycareGroupResponse>
  row: DaycareAclRow
}

type FormState = {
  selectedGroups: DaycareGroupResponse[] | null
  hasStaffOccupancyEffect: boolean | null
  endDate: LocalDate | null
}

export default React.memo(function EditAclModal({
  onClose,
  permittedActions,
  endDateEditable,
  unitId,
  groups,
  row
}: Props) {
  const { i18n, lang } = useTranslation()
  const { employee, role } = row
  const groupOptions = useGroupOptions(groups)

  const [formData, setFormData] = useState<FormState>({
    selectedGroups: permittedActions.includes('UPDATE_STAFF_GROUP_ACL')
      ? groupOptions.filter((option) => row.groupIds.includes(option.id))
      : null,
    hasStaffOccupancyEffect: permittedActions.includes(
      'UPSERT_STAFF_OCCUPANCY_COEFFICIENTS'
    )
      ? employee.hasStaffOccupancyEffect
      : null,
    endDate: row.endDate
  })

  return (
    <MutateFormModal
      title={i18n.unit.accessControl.editDaycareAclModal.title}
      resolveMutation={updateGroupAclMutation}
      resolveAction={() => ({
        unitId,
        employeeId: row.employee.id,
        body: {
          role,
          groupIds: formData.selectedGroups?.map(({ id }) => id) ?? null,
          hasStaffOccupancyEffect: formData.hasStaffOccupancyEffect,
          endDate: formData.endDate
        }
      })}
      resolveLabel={i18n.common.save}
      rejectAction={onClose}
      rejectLabel={i18n.common.cancel}
      onSuccess={onClose}
      data-qa="edit-acl-modal"
    >
      <FixedSpaceColumn>
        <FixedSpaceColumn $spacing="xs">
          <Label>{i18n.unit.accessControl.name}</Label>
          <div>
            <PersonName person={row.employee} format="First Last" />
          </div>
        </FixedSpaceColumn>
        <FixedSpaceColumn $spacing="xs">
          <Label>{i18n.unit.accessControl.role}</Label>
          <div>{i18n.roles.adRoles[role]}</div>
        </FixedSpaceColumn>

        {permittedActions.includes('READ_STAFF_OCCUPANCY_COEFFICIENTS') && (
          <Checkbox
            data-qa="coefficient-checkbox"
            checked={formData.hasStaffOccupancyEffect === true}
            disabled={
              !permittedActions.includes('UPSERT_STAFF_OCCUPANCY_COEFFICIENTS')
            }
            label={i18n.unit.accessControl.hasOccupancyCoefficient}
            onChange={(checked) => {
              setFormData({
                ...formData,
                hasStaffOccupancyEffect: checked
              })
            }}
          />
        )}

        <SectionRuler />

        {endDateEditable && (
          <FixedSpaceColumn $spacing="xs">
            <Label>{`${i18n.unit.accessControl.aclEndDate}`}</Label>
            <DatePicker
              data-qa="end-date"
              date={formData.endDate}
              onChange={(date) =>
                setFormData((prev) => ({ ...prev, endDate: date }))
              }
              locale={lang}
              minDate={LocalDate.todayInHelsinkiTz()}
            />
            <ExtraSpan>{i18n.unit.accessControl.aclEndDateHelp}</ExtraSpan>
          </FixedSpaceColumn>
        )}

        <SectionRuler />

        {permittedActions.includes('UPDATE_STAFF_GROUP_ACL') && (
          <FixedSpaceColumn $spacing="xs">
            <Label>{i18n.unit.accessControl.chooseGroup}</Label>
            <MultiSelect
              data-qa="group-select"
              value={formData.selectedGroups ?? []}
              options={groupOptions}
              getOptionId={(item) => item.id}
              getOptionLabel={(item) => item.name}
              onChange={(values) =>
                setFormData({ ...formData, selectedGroups: values })
              }
              placeholder={`${i18n.common.select}...`}
            />
            <ExtraSpan>{i18n.unit.accessControl.chooseGroupHelp}</ExtraSpan>
          </FixedSpaceColumn>
        )}
      </FixedSpaceColumn>
    </MutateFormModal>
  )
})

const ExtraSpan = styled.span`
  font-size: 14px;
  color: ${(p) => p.theme.colors.grayscale.g70};
  font-weight: 600;
`

const SectionRuler = styled(HorizontalLine)`
  margin-block-start: ${defaultMargins.xs};
  margin-block-end: ${defaultMargins.s};
`
